/**
 * useWorkspaceDetailLogic - Custom hook for workspace detail page business logic
 * Extracted from WorkspaceDetailPage to centralize all business logic
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { debugUserId, getSafeUserId } from '@/modules/app/utils/userIdDebug';
import { useAiAnalysisWebhook } from '@/modules/app/hooks';
import { useKeywordCategorization } from '@/modules/app/hooks/useKeywordCategorization';
import { useAiAnalysisData } from '@/modules/app/hooks/useAiAnalysisData';
import { toast } from 'sonner';
import { debugLog } from '@/lib/debugUtils';
import { BulkExportData } from '@/components/ui/BulkExportButton';

interface WorkspaceKeywordData {
  id: string;
  keyword?: string;
  primary_keyword: string;
  data_type: string;
  search_volume?: number;
  intent?: string;
  competition?: string;
  difficulty?: number;
  cpc?: number;
  funnel_stage?: string;
  created_at: string;
  subtopic?: string;
  people_also_ask?: string;
  answer?: string;
  url?: string;
  title?: string;
  domain?: string;
}

interface WorkspaceDetailState {
  workspace: any | null;
  keywordData: WorkspaceKeywordData[];
  allKeywordData: WorkspaceKeywordData[];
  isLoading: boolean;
  error: string | null;
  searchFilter: string;
  activeMainTab: string;
}

export const useWorkspaceDetailLogic = (workspaceId: string | undefined) => {
  const navigate = useNavigate();
  const { userId, isAuthenticated, user } = useAuth();
  
  const [state, setState] = useState<WorkspaceDetailState>({
    workspace: null,
    keywordData: [],
    allKeywordData: [],
    isLoading: true,
    error: null,
    searchFilter: '',
    activeMainTab: 'keyword-research'
  });

  // AI Analysis Hook
  const {
    triggerWebhook: triggerAiAnalysis,
    isLoading: isAiAnalysisLoading,
    error: aiAnalysisError,
    getErrorMessage: getAiErrorMessage,
    clearError: clearAiError
  } = useAiAnalysisWebhook();

  // AI Analysis Data Hooks for bulk export
  const { categorizations } = useKeywordCategorization(workspaceId || '', userId || '');
  const { data: aiData } = useAiAnalysisData({ workspaceId: workspaceId || '' });

  // Handle workspace name update
  const handleWorkspaceNameUpdate = useCallback((newName: string) => {
    setState(prev => ({ 
      ...prev, 
      workspace: prev.workspace ? { ...prev.workspace, name: newName } : null 
    }));
  }, []);

  // Handle workspace deletion - redirect to workspace list
  const handleWorkspaceDelete = useCallback((workspaceId: string) => {
    navigate('/app/workspace');
  }, [navigate]);

  // Enhanced AI Analysis action with comprehensive debugging
  const handleWorkspaceAiAnalysis = useCallback(async () => {
    const userEmail = user?.primaryEmailAddress?.emailAddress;
    debugLog(userEmail, '🚀 ========================================');
    debugLog(userEmail, '🚀 AI ANALYSIS BUTTON CLICKED');
    debugLog(userEmail, '🚀 ========================================');
    
    // Step 1: Environment Check
    const webhookUrl = import.meta.env.VITE_AI_ANALYSIS_WEBHOOK;
    debugLog(userEmail, '🔍 Environment Check:', {
      webhookUrl: webhookUrl || 'NOT SET',
      hasWebhookUrl: !!webhookUrl,
      mode: import.meta.env.MODE,
      timestamp: new Date().toISOString()
    });

    // Step 2: Basic State Check - Use ALL keyword data for analysis
    debugLog(userEmail, '🔍 Component State:', {
      workspaceId,
      workspaceName: state.workspace?.name,
      keywordDataLength: state.keywordData.length,
      allKeywordDataLength: state.allKeywordData.length,
      isAuthenticated,
      userId: userId ? 'SET' : 'NOT SET',
      isAiAnalysisLoading
    });

    // Step 3: Button State Check - Use ALL data for validation
    const isButtonDisabled = isAiAnalysisLoading || state.allKeywordData.length === 0;
    debugLog(userEmail, '🔍 Button State:', {
      isDisabled: isButtonDisabled,
      reason: isAiAnalysisLoading ? 'Loading' : state.allKeywordData.length === 0 ? 'No Keywords' : 'Enabled'
    });

    // Step 4: Early Exit Checks
    if (!workspaceId || !state.workspace) {
      debugLog(userEmail, '❌ Missing workspace data');
      toast.error('Workspace not available for analysis');
      return;
    }

    if (state.allKeywordData.length === 0) {
      debugLog(userEmail, '❌ No keyword data available');
      toast.error('No keyword data found. Please run a keyword search first.');
      return;
    }

    if (!isAuthenticated || !userId) {
      debugLog(userEmail, '❌ Authentication issue');
      toast.error('Please log in to use AI analysis');
      return;
    }

    if (!webhookUrl) {
      debugLog(userEmail, '❌ Missing webhook URL');
      toast.error('AI analysis service not configured. Please check environment variables.');
      return;
    }

    debugLog(userEmail, '✅ All checks passed - proceeding with analysis');

    try {
      // Step 5: Show immediate feedback
      toast.info('Starting AI Analysis...', { duration: 3000 });
      
      // Step 6: Clear previous errors
      clearAiError();

      // Updated parameters for workspace analysis
      const analysisParams = {
        workspace_id: workspaceId,
        workspace_name: state.workspace.name,
        analysis_type: 'workspace_analysis' as const,
        workflow_type: 'ai_analysis' as const,
        language_name: 'English',
        location_name: 'United States',
        include_data: true
      };

      debugLog(userEmail, '🚀 Analysis Parameters:', analysisParams);
      debugLog(userEmail, '🚀 Calling triggerAiAnalysis...');

      // Step 7: Trigger the analysis
      const response = await triggerAiAnalysis(analysisParams);

      debugLog(userEmail, '✅ AI Analysis Response:', response);
      toast.success('AI Analysis Started Successfully!');

    } catch (error: any) {
      console.error('❌ AI Analysis Error:', error); // Keep actual errors for all users
      console.error('❌ Error Details:', {
        name: error.name,
        message: error.message,
        type: error.type,
        stack: error.stack
      });

      // Show user-friendly error
      const errorMessage = getAiErrorMessage();
      toast.error('AI Analysis Failed', {
        description: errorMessage || error.message || 'Unknown error occurred'
      });
    }
    
    debugLog(userEmail, '🚀 ========================================');
    debugLog(userEmail, '🚀 AI ANALYSIS HANDLER COMPLETE');
    debugLog(userEmail, '🚀 ========================================');
  }, [
    workspaceId, 
    state.workspace, 
    state.allKeywordData,
    isAuthenticated,
    userId,
    isAiAnalysisLoading,
    triggerAiAnalysis,
    getAiErrorMessage,
    clearAiError,
    user
  ]);

  // Prepare bulk export data
  const prepareBulkExportData = useCallback((): BulkExportData => {
    // Group keyword data by type
    const groupedKeywordData = state.allKeywordData.reduce((acc, item) => {
      const type = item.data_type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {} as Record<string, any[]>);

    // Calculate total records including AI analysis
    const totalRecords = state.allKeywordData.length + 
                        categorizations.length + 
                        aiData.keywordClusters.length + 
                        aiData.contentBriefs.length;

    return {
      keywordResearch: {
        related: groupedKeywordData['related_keywords'] || [],
        ideas: groupedKeywordData['keyword_ideas'] || [],
        suggestions: groupedKeywordData['keyword_suggestions'] || [],
        subtopics: groupedKeywordData['subtopics'] || [],
        paa: groupedKeywordData['people_also_ask'] || [],
        trends: groupedKeywordData['google_trends'] || [],
        serp: groupedKeywordData['serp_analysis'] || [],
      },
      aiAnalysis: {
        categories: categorizations || [],
        clusters: aiData.keywordClusters || [],
        contentIdeas: aiData.contentBriefs || [],
      },
      metadata: {
        workspaceName: state.workspace?.name || 'Unknown Workspace',
        exportDate: new Date().toISOString(),
        totalRecords: totalRecords
      }
    };
  }, [state.allKeywordData, state.workspace, categorizations, aiData]);

  // Handle search filter change
  const handleSearchFilterChange = useCallback((value: string) => {
    setState(prev => ({ ...prev, searchFilter: value }));
  }, []);

  // Check if there's data for export
  const hasDataForExport = state.allKeywordData.length > 0 || 
                          categorizations.length > 0 || 
                          aiData.keywordClusters.length > 0 || 
                          aiData.contentBriefs.length > 0;

  // Fetch workspace details and keyword data
  useEffect(() => {
    const fetchWorkspaceData = async () => {
      if (!workspaceId || !userId) return;

      // Debug the user ID being used
      debugUserId(userId, 'WorkspaceDetailPage.fetchWorkspaceData');
      
      // Get safe user ID for database operations
      const safeUserId = getSafeUserId(userId, 'WorkspaceDetailPage.validation');
      if (!safeUserId) {
        setState(prev => ({
          ...prev,
          error: 'User authentication failed. Please refresh the page.',
          isLoading: false
        }));
        return;
      }

      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const userEmail = user?.primaryEmailAddress?.emailAddress;
        debugLog(userEmail, '[WorkspaceDetailPage] Fetching workspace data with validated user ID:', safeUserId);
        
        // Fetch workspace details
        const { data: workspaceData, error: workspaceError } = await supabase
          .from('workspaces')
          .select('*')
          .eq('id', workspaceId)
          .eq('user_id', safeUserId)
          .single();

        if (workspaceError) throw workspaceError;
        
        // Check if workspace is deleted
        if (workspaceData.name.startsWith('[DELETED]')) {
          setState(prev => ({
            ...prev,
            error: 'This workspace has been deleted.',
            isLoading: false
          }));
          return;
        }

        // Fetch ALL keyword data for this workspace
        const { data: keywordResults, error: keywordError } = await supabase
          .from('keyword_consolidated_data')
          .select('*')
          .eq('workspace_id', workspaceId)
          .eq('user_id', safeUserId)
          .order('created_at', { ascending: false });

        if (keywordError) throw keywordError;
        const allData = keywordResults || [];
        
        setState(prev => ({
          ...prev,
          workspace: workspaceData,
          allKeywordData: allData,
          keywordData: allData.slice(0, 500), // Keep display data limited for UI performance
          isLoading: false
        }));
        
        debugLog(userEmail, `📊 Fetched ${allData.length} total keywords, displaying first 500 in UI`);
        debugLog(userEmail, '📊 Data types found:', [...new Set(allData.map(k => k.data_type))]);

      } catch (err: any) {
        console.error('Error fetching workspace data:', err);
        setState(prev => ({
          ...prev,
          error: err.message || 'Failed to load workspace data',
          isLoading: false
        }));
      }
    };

    fetchWorkspaceData();
  }, [workspaceId, userId, user]);

  return {
    // State
    ...state,
    
    // AI Analysis
    isAiAnalysisLoading,
    aiAnalysisError,
    getAiErrorMessage,
    clearAiError,
    
    // Computed values
    prepareBulkExportData,
    hasDataForExport,
    
    // Handlers
    handleWorkspaceNameUpdate,
    handleWorkspaceDelete,
    handleWorkspaceAiAnalysis,
    handleSearchFilterChange,
  };
};